import { createMachine, assign } from 'xstate'

import { trackQuizPageViewed } from 'libs/events'
import { type LearningOptionType } from './types'
import type { TCategory, TSubcategory } from '../../types'

export const CATEGORIES_URL_PARAM = 'categories'
export const SUBCATEGORIES_URL_PARAM = 'subcategories'
const MOTIVATION_REFINEMENT_VIEWED = 'mr'
const WAS_VIEWED = 't'

export enum STEP_NAME {
  PRIMER_QUESTION = 'primer-question',
  MOTIVATION_REFINEMENT = 'motivation-category-refinement',
  TIME_QUESTION = 'time-interstitial',
  SUBCATEGORY_QUESTION = 'subcategory-question',
  FULL_ACCESS_INTERSTITIAL = 'full-access-interstitial',
}

type Context = {
  stepType: 'interactive' | 'interstitial'
  userHasSkipQ1Param: boolean
  step: string
  stepHistory: string[]
  stepName: STEP_NAME
  interstitialScreen?:
    | 'full access'
    | 'learning time'
    | 'classes for you'
    | 'bogo explainer'
  timeSpent?: LearningOptionType
  totalSteps: number
  categoryInterestsCSV: string | undefined
  allCategories: TCategory[]
  allSubcategories: TSubcategory[]
  catsToRefineSubcats?: number[]
  subcatInterests?: number[]
  isBogoOfferEligible?: boolean
  userHasSkipMotivationRefinementParam: boolean
  delayDuration?: number
  isVideosInQuizVariant1: boolean // pen-4868_v1
}

type TimeSpentEvent = {
  machineEventType: 'time_spent'
  timeSpent?: LearningOptionType
}

type SubcatRefinementEvent = {
  machineEventType: 'subcat_refinement'
  categoryIdRefined: number
  subcatInterests: number[]
}

type DelayDurationEvent = {
  delayDuration: number
}

export type NextEventParameters =
  | TimeSpentEvent
  | SubcatRefinementEvent
  | undefined

type Events =
  | {
      type: 'next'
      params?: NextEventParameters
    }
  | { type: 'back' }
  | { type: 'end' }
  | { type: 'set'; params?: DelayDurationEvent }

const LOADING = 'loading'
const TRANSIENT = 'transient'
const INTERACTIVE = 'interactive'
const INTERSTITIAL = 'interstitial'
export const TAGS = {
  Loading: LOADING,
  Transient: TRANSIENT,
  Interactive: INTERACTIVE,
  Interstitial: INTERSTITIAL,
} as const

/**
 * This is the XState definition for the quiz machine.
 * It is strongly recommended you install the XState VSCode extension.
 * This will give you access to the "Visual Editor"
 * as well as typegen functionality to strongly type the states, etc.
 *
 * https://marketplace.visualstudio.com/items?itemName=statelyai.stately-vscode
 */
export const quizMachine = createMachine(
  {
    /** @xstate-layout N4IgpgJg5mDOIC5QEcCuBLAXgWQIYGMALdAOzADpT0AXdXAGyzAGIBtABgF1FQAHAe1g10-EjxAAPRAFoAzABZyATnYqlsgEyyA7PPnaNGgDQgAnjICMANgCsADnLy7hg3Yt3t67QF9vJtFh4RKQUVLQMTGwW3EggAkK0ouJSCHbs5B4a2hbaslbsHpp2JuYI0hY2NrLk+VbyFhaqbrKyNr7+GDgExGSUJMIRmCysGjF8gsJJsSnaNjVa+kp2adpphiWWelbK8pUe2uy1S+0gAV3Bvfi41GBQ-ABOpgCSJDf3cNSwzABGBADWHDGcQmiTE00sngyrSsdXYNiUFnkdXkGzKdnU5ERsgsKi2Vg0ThOZyCPQoVxud0eLzeHy+ZAk1EB4nikzBoBS8jhNWc7FWS1UBiUVlR5Xccw0NhyGnY1iqOiJnRJIXIADNUPR6ABBfD4OB0sAMpmxFmg5KIepKJSOGxWWaNGx87QihHbJSeLRWBRZOEKwLdZVqjXa3WwL6-fAArjMkEiNmSRAKXLkdgFWS8rLyHRKEX46rsLKWpROfLyLS+86k1XqrU6vVsKPGmNTdkJgyKOr2T02FN5TQiho6chVIvotN2Ql+U6K-29b78O4AeRVKrA92pq9gtHC9GY9MZDfGCVjZoQNmlcyFtiUWiqdSUNmdFg0GXykrvCnx5aVs-n-CXK7XV4Ny3Ogd3DSMgRNY9wQQUsCnIXQrAsWRcSFSoRQOZ87BaAwdEyS0vxnCh6DAXB7n6EgoAAFXQABbFg9yNQ9WRPfFLXmQ4WnYTRtGFMwZHURRVHvHRkUqRFCIuYjSPI0hqLolhwKY4Ej2beNT1WbQEMvZwrHUKoUX4spsm2G8vXsN1NMkysSLIij5Pon5-lYaJo1UuMUnsREaj0VpkPcIoRU7TEhX0OpxwMJ9rOVWzZMomjHKU0Y3JYmD2FRdhot6fgAHcSAABQIBiDX3SCmw8xADjsOZ4WlOFtFmTRbBFdEtLqZFbVaNN5UnYkiPIXKCqKpyI2UqC1JSW1uOTWRqvcA5PQKDDuwQjQGnRdwCjbLKyX4WjeBIm4AEVOmYWAwFKlLTTSjKdvIfA9oOi6wBOrByHO6hUF4XcSrG8qT3SozMt66cpPux7DpezpyFQEhcH6WjrkgZgJE3JHyFwFU3gACgAEQAUQAGU1ABNABKZg+rBh79sh17MBhuGEaRiA-vcgHbpBv1qYh576Yx5naEolG0ZuDGsdXPGidJimqcrGmnuO6H4bo645LZ1KWwQQHSmBjpufl3mlbelXEZuCAReodHMZxgnifJynQcN2m+eVwXIA166tZ1xA9anA3lQVunoYgMB6FwUxkdRq2xZtyW7Zlx2A8uI2obe0Pw8j1mDxUzX1J97W7tgVBvnJdd3k3fVDRz8aKoQbEsnICV7QCpEGudM8EMlDQYWxKp8ysIuS7LoCK8+H7DVcxt2ZgzR8x2awnF5FMnSM6QGq03ibVmGUPx7ofS+ucvaRGiCrugrXrCTG12FLRpGlsQzSmkdR0hhGU7E9OpWgRA+R5pSup8XJlRnpfFQcwdBPgUCoNIiJjBryqsmCo0JLQKEzLIO6c5FzLlXPWEBecOS6AsBkGUGhnAoXhM1NeDRsjJnxFkGEWIxSYN-P+XBwDz4TUQN2ZwTdlgNBlLmcc-YcjEMOC4RhmZmFcwrMqWi-BaAADc1aiAAEpgBVCEeirxCowCATXf6MFsLVF0rYW0dRNoWGdLoZQHghQ5BTPUXkd15FKJUSQdRmiyDaOoLo4q1d8Fe3UsheCsxdA9xyNiJ8T9LCIgge4ccdRViIkOH-a4sAXgDEYJgdxeDOF1wlPPDQSxnBkLqD3B0Ij8jJglFoccWReI+BOCQfgod4CxDliEfJJ515IgQthN0OI3QOlmv2NI6QnBrRTCWaUdg7phFAkwbpMFpB6QcKsFC2RLRhNGdQ7Iz4liGGlJyfI2Q2gyO-GSJGlJnij1pMsrWvTyB5EMGmRoeR8SVOoYU55EUdB5HqRgi5-VAw1hDO05iQSUitGWDNQ4NgnAIsMMUNe95TLFLyPobs+gfTArBlgv8ODAJvE3Fkh56l9CNExE4BExTOQyhiWUNFTdilaGKc4ShTT9ayN6LFeyCUwDks8gcCZqgEVOBaFi50Tg6GZjlH5BoGg7qDUKrqIViAYS8MlMsbe9gpGPmIboVY8h1D4mQkKO6QdXZYHVWUJw-YYTJhTGtZYcI8jqEtanfmH0vq2oLhavFztFZpwZrDU2LM-Woi0OkbszqlhFglLNOZgbA5erdqrIWUBI1GTyHMFMKZ4SrGqryBFnqXbGwZuG822bSgmqtLGgoQpnC8SiimlO5aQ3kAzhHSANaNUKCdTKHEDoDiIiUGW4NEBbWrKWP0zZQydkotKG4bYsa6rdgobkNJ1Bj6V1tVoVojhOScl4mQhoHdXQtDPLfIsT5qosOwQBW19Q6jPPflePQBR4HPwaCahCekFhIU8D2O6hA9pgD8dOz01Q9BZGQfYAFfFn7qGqN-SJhbPAuIUegZRoJPFaLADo3AMBbVuAvBYnIHhJTcVkP2I5R7rCtElHBWY26Mn9G3FgdxtrrCNwOJAtwn8TV9m+Q0JujUsQDN2PIXwvggA */
    id: 'quizMachine',
    tsTypes: {} as import('./machine.typegen').Typegen0,
    schema: {
      context: {} as Context,
      events: {} as Events,
    },
    initial: 'initialize',
    states: {
      initialize: {
        description: 'This is the mandatory entry state of the quiz.',
        tags: [LOADING],
        entry: ['trackQuizPageViewed', 'clientUpdateUrlToContext'],
        always: [
          {
            // pen-4868_v1 variant_1
            target: 'fullAccess',
            cond: 'isVideosInQuizVar1AndSkipToSubcatInterests',
          },
          {
            target: 'subcatsInitialization',
            cond: 'userShouldSkipToSubcatInterests',
          },
          {
            target: 'motivationRefinementPage',
            cond: 'userShouldSeeMotivationRefinement',
          },
          'categoryInterests',
        ],
      },

      categoryInterests: {
        entry: 'setMotivationInteractiveData',
        exit: 'initializeSubcatRefinements',
        description: 'Interactive step "What brings you to MasterClass today"',
        tags: [INTERACTIVE],

        on: {
          back: {
            cond: 'userDoesNotHaveSkipQ1Param',
            target: 'homePage',
          },

          next: [
            {
              target: 'motivationRefinementPage',
              actions: 'incrementInteractiveStepNumber',
            },
          ],
        },
      },

      fullAccess: {
        entry: 'onFullAccessEntry',
        description: 'Interstitial step "Did you know?"',
        tags: [INTERSTITIAL],

        on: {
          next: [
            {
              // pen-4868_v1 variant_1
              cond: 'isVideosInQuizVariant1',
              target: 'subcatsInitialization',
            },
            { target: 'bogoOffer' },
          ],

          back: [
            {
              // pen-4868_v1 variant_1 - replicate back logic of subcatInterests step
              cond: 'isVideosInQuizVariant1',
              target: 'motivationRefinementPage',
              actions: 'decrementStepNumber',
            },
            {
              target: 'subcatInterests',
              actions: 'removeLastCategoryFromRefined',
            },
          ],
        },
      },

      bogoOfferInterstitial: {
        entry: 'setBogoOfferInterstitialStepData',
        description: 'Display BOGO offer promo',
        tags: [INTERSTITIAL],
        on: {
          next: [
            {
              target: 'learningTime',
              actions: 'incrementInteractiveStepNumber',
            },
          ],
          back: [
            {
              // pen-4868_v1 variant_1 - replicate back logic of full access step
              cond: 'isVideosInQuizVariant1',
              target: 'subcatInterests',
              actions: ['removeLastCategoryFromRefined', 'decrementStepNumber'],
            },
            {
              target: 'fullAccess',
              actions: 'decrementStepNumber',
            },
          ],
        },
      },

      learningTime: {
        entry: 'setLearningTimeStepData',
        description:
          'Interactive step "How much time do you want to spend learning"',
        tags: [INTERACTIVE],
        on: {
          next: {
            target: 'ownPace',
            actions: ['setTimeSpent', 'incrementInterstitialStepNumber'],
          },
          back: [
            {
              cond: 'isBogoOfferEnabled',
              target: 'bogoOfferInterstitial',
              actions: 'decrementStepNumber',
            },
            {
              // pen-4868_v1 variant_1 - replicate back logic of full access step
              cond: 'isVideosInQuizVariant1',
              target: 'subcatInterests',
              actions: ['removeLastCategoryFromRefined', 'decrementStepNumber'],
            },
            {
              target: 'fullAccess',
              actions: 'decrementStepNumber',
            },
          ],
        },
      },

      ownPace: {
        entry: 'setOwnPaceInterstitialData',
        description: 'Interstitial step "Did you know?"',
        tags: [INTERSTITIAL],
        on: {
          next: [
            {
              target: 'completeQuiz',
            },
          ],
          back: {
            target: 'learningTime',
            actions: 'decrementStepNumber',
          },
        },
      },

      completeQuiz: {
        entry: 'prepareCompletion',
        description:
          'This state prepares the quiz exit by showing a custom loading page',
        initial: 'setup',
        states: {
          setup: {
            on: {
              next: { actions: 'setDelayDuration', target: 'unanimated' },
            },
          },
          unanimated: {
            after: { DELAY: { target: 'animating' } },
          },
          animating: {
            after: { DELAY: { target: 'animated' } },
          },
          animated: {
            after: { DELAY: { target: 'delayed' } },
          },
          delayed: {
            after: { DELAY: { target: '#quizMachine.completed' } },
          },
        },
        on: { set: { actions: 'setDelayDuration' } },
      },

      completed: {
        entry: 'quizCompleted',
        description: 'This state redirects to the class recs results',
        type: 'final',
      },

      subcatInterests: {
        description:
          'Interactive step: Select subcategory interests. Repeats until all necessary categories have been refined with subcat interests.',
        tags: [INTERACTIVE],
        entry: ['onSubcatInterestsEntry'],

        on: {
          next: [
            // pen-4868_v1 variant_1 - skip full access step when all categories have been subcat refined
            {
              target: 'bogoOffer',
              cond: 'isVideosInQuizVar1AndAllCatsSubcatRefined',
              actions: 'updateSubcatRefinements',
            },
            {
              target: 'fullAccess',
              cond: 'allCategoriesHaveBeenSubcatRefined',
              actions: 'updateSubcatRefinements',
            },
            {
              target: 'subcatInterests',
              internal: true,
              actions: 'updateSubcatRefinements',
            },
          ],

          back: [
            {
              // pen-4868_v1 variant_1
              target: 'fullAccess',
              cond: 'isVideosInQuizVar1AndNoCategoriesSubcatRefined',
              actions: 'decrementStepNumber',
            },
            {
              target: 'motivationRefinementPage',
              cond: 'noCategoriesHaveBeenSubcatRefined',
              actions: 'decrementStepNumber',
            },
            {
              target: 'subcatInterests',
              actions: 'removeLastCategoryFromRefined',
            },
          ],
        },
      },

      bogoOffer: {
        description:
          '**Transient state**: handle state transitions when bogo offer is on',
        tags: [TRANSIENT],
        always: [
          {
            target: 'bogoOfferInterstitial',
            cond: 'isBogoOfferEnabled',
            actions: 'incrementInterstitialStepNumber',
          },
          {
            target: 'learningTime',
            actions: 'incrementInteractiveStepNumber',
          },
        ],
      },

      homePage: {
        entry: 'goToHomePage',
        description: 'This state redirects to the homepage after going back',
        type: 'final',
        tags: [TRANSIENT],
      },

      motivationRefinementPage: {
        description:
          'Motivation refinement (select specific category interests)',
        entry: 'onMotivationRefinementEntry',
        exit: [
          'initializeSubcatRefinements',
          'setMotivationRefinementPageViewed',
        ],
        tags: [INTERACTIVE],

        on: {
          back: {
            target: 'categoryInterests',
            actions: 'decrementStepNumber',
          },
          next: [
            {
              // pen-4868_v1 variant_1
              cond: 'isVideosInQuizVariant1',
              target: 'fullAccess',
            },
            { target: 'subcatsInitialization' },
          ],
        },
      },

      subcatsInitialization: {
        description:
          '**Transient state**: Entry state for subcat step. Sets up machine to handle category subcat refinements',

        tags: [TRANSIENT],
        exit: 'initializeSubcatRefinements',

        always: ['subcatInterests'],
      },
    },
    predictableActionArguments: true,
  },
  {
    actions: {
      // no-op actions to satisfy TypeScript; to implement in `customActions` (Quiz.tsx)
      quizCompleted: () => null,
      goToHomePage: () => null,
      prepareCompletion: () => null,
      // end no-op actions
      incrementInteractiveStepNumber: assign((ctx) => {
        const newStep = getNewStepNumber(ctx.step, 1, 0)
        return {
          step: newStep,
          stepHistory: [...ctx.stepHistory, newStep],
        }
      }),
      incrementInterstitialStepNumber: assign((ctx) => {
        const newStep = getNewStepNumber(ctx.step, 0, 1)
        return {
          step: newStep,
          stepHistory: [...ctx.stepHistory, newStep],
        }
      }),
      decrementStepNumber: assign((ctx) => {
        const newStepHistory = ctx.stepHistory.slice(0, -1)
        return {
          step: newStepHistory.at(-1),
          stepHistory: newStepHistory,
        }
      }),
      trackQuizPageViewed: (ctx) =>
        trackQuizPageViewed({
          promo_banner_visible: !!ctx.isBogoOfferEligible,
        }),
      setMotivationInteractiveData: assign({
        stepType: 'interactive',
        stepName: STEP_NAME.PRIMER_QUESTION,
        step: '1',
        interstitialScreen: undefined,
      }),
      onMotivationRefinementEntry: assign({
        stepType: INTERACTIVE,
        stepName: STEP_NAME.MOTIVATION_REFINEMENT,
        step: '2',
        interstitialScreen: undefined,
      }),
      onSubcatInterestsEntry: assign({
        stepType: 'interactive',
        stepName: STEP_NAME.SUBCATEGORY_QUESTION,
        interstitialScreen: undefined,
        step: '3',
      }),
      onFullAccessEntry: assign({
        stepType: 'interstitial',
        interstitialScreen: 'full access',
        stepName: STEP_NAME.FULL_ACCESS_INTERSTITIAL,
      }),
      setLearningTimeStepData: assign({
        stepType: 'interactive',
        stepName: STEP_NAME.TIME_QUESTION,
        interstitialScreen: undefined,
        step: '4',
      }),
      setBogoOfferInterstitialStepData: assign({
        stepType: 'interstitial',
        interstitialScreen: 'bogo explainer',
      }),
      setOwnPaceInterstitialData: assign({
        stepType: 'interstitial',
        interstitialScreen: 'learning time',
      }),
      setTimeSpent: assign((_, event) => ({
        timeSpent:
          event.type === 'next' &&
          event.params?.machineEventType === 'time_spent'
            ? event.params.timeSpent
            : undefined,
      })),
      clientUpdateUrlToContext: assign({
        categoryInterestsCSV: (ctx) =>
          getCategoryInterestsUrlParam() ?? ctx.categoryInterestsCSV,
      }),
      setMotivationRefinementPageViewed: assign(() => {
        // user came from motivation refinement screen
        // so we mark it as viewed in the URL
        updateQueryParam(MOTIVATION_REFINEMENT_VIEWED, WAS_VIEWED)
        return { userHasSkipMotivationRefinementParam: true }
      }),
      initializeSubcatRefinements: assign((ctx) => {
        const categoryInterestsCSV =
          getCategoryInterestsUrlParam() ?? ctx.categoryInterestsCSV
        const allCatsToRefineSubcats = getCategoryInterests(
          categoryInterestsCSV || '',
          ctx.allCategories,
        )
        // at any moment, the subcats selected should reflect the categories selected.
        // e.g. if a user specifies interest in category 1 then selects subcats within it
        // then they go back, remove category 1 interest and select new subcats
        // we should NOT have any subcats from the no-longer selected category 1
        const validSubcats = new Set(
          ctx.allSubcategories
            .filter(
              (s) =>
                !!s.parentId && allCatsToRefineSubcats.includes(s.parentId),
            )
            .map((s) => s.id),
        )

        const allSCs = getSubcatInterestsUrlParam() || ctx.subcatInterests || []
        const subcatInterests = allSCs.filter((sc) => validSubcats.has(sc))

        return {
          categoryInterestsCSV,
          catsToRefineSubcats: allCatsToRefineSubcats,
          subcatInterests,
        }
      }),
      updateSubcatRefinements: assign({
        subcatInterests: (ctx, event) => {
          if (event?.params?.machineEventType === 'subcat_refinement') {
            const { params } = event
            const { allSubcategories } = ctx

            const scIds = new Set(
              allSubcategories
                .filter((s) => s.parentId === params.categoryIdRefined)
                .map((s) => s.id),
            )

            // for users who go back and update;
            // the "previous" subcat selections should NOT include
            // any subcats that are in the current category. because
            // the new subcat interests (for the current category)
            // will override all those previous selections
            const currentCatSubcats = (scId: number) => !scIds.has(scId)
            const prev = (ctx.subcatInterests || []).filter(currentCatSubcats)
            const newSubcatInterests = [...prev, ...params.subcatInterests]
            updateQueryParam(
              SUBCATEGORIES_URL_PARAM,
              newSubcatInterests.join(','),
            )
            return newSubcatInterests
          }
          return undefined
        },
        catsToRefineSubcats: (ctx, event) => {
          if (event.type !== 'next') {
            return undefined
          }
          return ctx.catsToRefineSubcats?.filter((catId) => {
            if (event.params?.machineEventType !== 'subcat_refinement') {
              return false
            }
            return catId !== event.params.categoryIdRefined
          })
        },
      }),
      removeLastCategoryFromRefined: assign({
        catsToRefineSubcats: (ctx) => {
          // basically we want to re-add the category id that was removed
          // start with the original sorted order
          const allCats = getCategoryInterests(
            ctx.categoryInterestsCSV || '',
            ctx.allCategories,
          )
          // get already refined categories
          const unrefinedCats = ctx.catsToRefineSubcats || []
          const refinedCats = allCats.filter(
            (catId) => !unrefinedCats?.includes(catId),
          )
          // add last refined cat to front of unrefined list
          const lastRefinedCat = refinedCats[refinedCats.length - 1]
          return [lastRefinedCat, ...unrefinedCats]
        },
      }),
      setDelayDuration: assign((_, event) => ({
        delayDuration:
          event.type === 'set' ? event.params?.delayDuration : undefined,
      })),
    },
    guards: {
      allCategoriesHaveBeenSubcatRefined: ({ catsToRefineSubcats }, event) => {
        const allCatsWithoutEvent = (catsToRefineSubcats || []).filter(
          (catId) => {
            if (event?.params?.machineEventType === 'subcat_refinement') {
              return catId !== event.params.categoryIdRefined
            }
            return false
          },
        )

        return allCatsWithoutEvent.length === 0
      },
      noCategoriesHaveBeenSubcatRefined: (c) => noSubcatRefinedCategories(c),
      isBogoOfferEnabled: (ctx) => !!ctx.isBogoOfferEligible,
      // pen-4868_v1
      isVideosInQuizVariant1: (ctx) => isVideosInQuizVariant1(ctx),
      isVideosInQuizVar1AndSkipToSubcatInterests: (ctx) =>
        isVideosInQuizVariant1(ctx) && userShouldSkipToSubcatInterests(ctx),
      isVideosInQuizVar1AndAllCatsSubcatRefined: (ctx, event) =>
        isVideosInQuizVar1AndAllCatsSubcatRefined(ctx, event),
      isVideosInQuizVar1AndNoCategoriesSubcatRefined: (ctx) =>
        isVideosInQuizVariant1(ctx) && noSubcatRefinedCategories(ctx),
      // end pen-4868_v1
      userShouldSkipToSubcatInterests: (c) =>
        userShouldSkipToSubcatInterests(c),
      userShouldSeeMotivationRefinement: (c) =>
        userShouldSeeMotivationRefinement(c),
      userDoesNotHaveSkipQ1Param: (ctx) => !ctx.userHasSkipQ1Param,
    },
    delays: {
      DELAY: ({ delayDuration }) => delayDuration ?? 0,
    },
  },
)

const getClientSearchParams = () => {
  if (typeof window !== 'undefined') {
    const search = window?.location?.search
    return new URLSearchParams(search)
  }
  return undefined
}

const updateQueryParam = (key: string, value: string) => {
  const qp = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : '',
  )
  qp.set(key, value)
  const history =
    typeof window !== 'undefined'
      ? window.history
      : {
          replaceState: () => {},
        }
  const query = qp.toString()
  history.replaceState(null, '', query ? `?${query}` : '')
}

const getCategoryInterestsUrlParam = () => {
  const categories = getClientSearchParams()?.get(CATEGORIES_URL_PARAM)
  if (typeof categories === 'string') return categories
  return undefined
}

const getSubcatInterestsUrlParam = () => {
  const subcats = getClientSearchParams()?.get(SUBCATEGORIES_URL_PARAM)
  if (typeof subcats === 'string') return parseCSVToNumberList(subcats)
  return undefined
}

const parseCSVToNumberList = (csv: string) =>
  csv
    .split(',')
    .map((idStr) => parseInt(idStr, 10))
    .filter((v) => !Number.isNaN(v))

const getCategoryInterests = (
  categoryInterestsCSV: string,
  cats: TCategory[],
) => {
  const allCategorySelections = parseCSVToNumberList(categoryInterestsCSV)
  allCategorySelections.sort((aID, bID) => {
    const a = cats.find((c) => c.id === aID)?.name || aID
    const b = cats.find((c) => c.id === bID)?.name || bID
    return a > b ? 1 : -1
  })
  return allCategorySelections
}

const setsEqual = <T>(xs: Set<T>, ys: Set<T>) =>
  xs.size === ys.size && [...xs].every((x) => ys.has(x))

const getNewStepNumber = (
  currentStep: string,
  stepDelta: number,
  subStepDelta: number,
) => {
  const [step, subStep = 0] = (currentStep || '0').split('.').map(Number)
  const newStep = step + stepDelta
  const newSubStep = stepDelta ? 0 : subStep + subStepDelta

  if (newSubStep > 0) {
    // Checks if newStep exists and > 0, to cover the case when quiz starts on a interstitial state.
    return `${newStep || 1}.${newSubStep}`
  }

  return `${newStep}`
}

const getCategoryInterestIds = (ctx: Context) =>
  getCategoryInterests(ctx.categoryInterestsCSV || '', ctx.allCategories)

const noSubcatRefinedCategories = (ctx: Context) => {
  // guard to decide "back" functionality on the subcat interests step
  // true => go back to the Overview/Skip page
  // false => go to refine the previously refined subcat
  const allCats = new Set(getCategoryInterestIds(ctx))
  const unrefinedCats = new Set(ctx.catsToRefineSubcats)
  return setsEqual(allCats, unrefinedCats)
}

const userShouldSeeMotivationRefinement = (ctx: Context) =>
  typeof ctx.categoryInterestsCSV === 'string' &&
  ctx.categoryInterestsCSV.length >= 1 &&
  ctx.userHasSkipQ1Param

const userShouldSkipToSubcatInterests = (ctx: Context) =>
  userShouldSeeMotivationRefinement(ctx) &&
  ctx.userHasSkipMotivationRefinementParam

// pen-4868_v1 variant_1
const isVideosInQuizVariant1 = (ctx: Context) => ctx.isVideosInQuizVariant1

const allCategoriesHaveBeenSubcatRefined = (
  { catsToRefineSubcats }: { catsToRefineSubcats: number[] },
  event: {
    type: 'next'
    params?: NextEventParameters
  },
) => {
  const allCatsWithoutEvent = (catsToRefineSubcats || []).filter((catId) => {
    if (event?.params?.machineEventType === 'subcat_refinement') {
      return catId !== event.params.categoryIdRefined
    }
    return false
  })

  return allCatsWithoutEvent.length === 0
}

const isVideosInQuizVar1AndAllCatsSubcatRefined = (
  ctx: Context,
  event: {
    type: 'next'
    params?: NextEventParameters
  },
) =>
  isVideosInQuizVariant1(ctx) &&
  allCategoriesHaveBeenSubcatRefined(
    { catsToRefineSubcats: ctx.catsToRefineSubcats || [] },
    event,
  )
// end pen-4868_v1 variant_1
